import { BaseActions } from '@/models/Common/Actions/BaseActions';
import { execAction } from '@/models/Common/Actions/handler';
import { ResponseEither } from '@/models/Common/ResponseEither';
import IMemberSocialFindRequestParams from '@/models/Member/interfaces/IMemberSocialFindRequestParams';
import IMemberSocialJoinRequestParams from '@/models/Member/interfaces/IMemberSocialJoinRequestParams';
import IMemberSocialLinkInfoRequestParams from '@/models/Member/interfaces/IMemberSocialLinkInfoRequestParams';
import IMemberSocialLinkRequestParams from '@/models/Member/interfaces/IMemberSocialLinkRequestParams';
import { IMemberSocialLinkValidateRequestParams } from '@/models/Member/interfaces/IMemberSocialLinkValidateRequestParams';
import IMemberSocialLoginV2RequestParams from '@/models/Member/interfaces/IMemberSocialLoginV2RequestParams';
import IMemberSocialLoginWithLinkRequestParams from '@/models/Member/interfaces/IMemberSocialLoginWithLinkRequestParams';
import IMemberSocialUnlinkRequestParams from '@/models/Member/interfaces/IMemberSocialUnlinkRequestParams';
import IMemberSocialFindPayload from '@/models/Member/interfaces/payloads/IMemberSocialFindPayload';
import IMemberSocialJoinPayload from '@/models/Member/interfaces/payloads/IMemberSocialJoinPayload';
import IMemberSocialLinkInfoPayload from '@/models/Member/interfaces/payloads/IMemberSocialLinkInfoPayload';
import IMemberSocialLinkPayload from '@/models/Member/interfaces/payloads/IMemberSocialLinkPayload';
import { IMemberSocialLinkValidatePayload } from '@/models/Member/interfaces/payloads/IMemberSocialLinkValidatePayload';
import IMemberSocialLoginV2Payload from '@/models/Member/interfaces/payloads/IMemberSocialLoginV2Payload';
import IMemberSocialLoginWithLinkPayload from '@/models/Member/interfaces/payloads/IMemberSocialLoginWithLinkPayload';
import IMemberSocialUnlinkPayload from '@/models/Member/interfaces/payloads/IMemberSocialUnlinkPayload';
import debug from 'debug';

import { IMemberChangeAgreeRequestParams } from './interfaces/IMemberChangeAgreeRequestParams';
import { IMemberChangeEmailRequestParams } from './interfaces/IMemberChangeEmailRequestParams';
import { IMemberChangeNickNameRequestParams } from './interfaces/IMemberChangeNickNameRequestParams';
import { IMemberChangePasswordRequestParams } from './interfaces/IMemberChangePasswordRequestParams';
import { IMemberCheckMilkConnectionRequestParams } from './interfaces/IMemberCheckMilkConnectionRequestParams';
import { IMemberConfirmPasswordRequestParams } from './interfaces/IMemberConfirmPasswordRequestParams';
import { IMemberLiveCommerceTokenRequestParams } from './interfaces/IMemberLiveCommerceTokenRequestParams';
import { IMemberMilkConnectRequestParams } from './interfaces/IMemberMilkConnectRequestParams';
import { IMemberMilkDisconnectRequestParams } from './interfaces/IMemberMilkDisconnectRequestParams';
import { IMemberMyBenefitRequestParams } from './interfaces/IMemberMyBenefitRequestParams';
import { IMemberUnsubscribeRequestParams } from './interfaces/IMemberUnsubscribeRequestParams';
import { IMemberWithdrawReasonListRequestParams } from './interfaces/IMemberWithdrawReasonListRequestParams';
import { IMemberWithdrawRequestParams } from './interfaces/IMemberWithdrawRequestParams';
import { IMemberChangeAgreePayload } from './interfaces/payloads/IMemberChangeAgreePayload';
import { IMemberChangeEmailPayload } from './interfaces/payloads/IMemberChangeEmailPayload';
import { IMemberChangeNickNamePayload } from './interfaces/payloads/IMemberChangeNickNamePayload';
import { IMemberChangePasswordPayload } from './interfaces/payloads/IMemberChangePasswordPayload';
import { IMemberCheckMilkConnectionPayload } from './interfaces/payloads/IMemberCheckMilkConnectionPayload';
import { IMemberConfirmPasswordPayload } from './interfaces/payloads/IMemberConfirmPasswordPayload';
import { IMemberLiveCommerceTokenPayload } from './interfaces/payloads/IMemberLiveCommerceTokenPayload';
import { IMemberMilkConnectPayload } from './interfaces/payloads/IMemberMilkConnectPayload';
import { IMemberMilkDisconnectPayload } from './interfaces/payloads/IMemberMilkDisconnectPayload';
import { IMemberMyBenefitPayload } from './interfaces/payloads/IMemberMyBenefitPayload';
import { IMemberUnsubscribePayload } from './interfaces/payloads/IMemberUnsubscribePayload';
import { MemberWithdrawPayload } from './interfaces/payloads/IMemberWithdrawPayload';
import { IMemberWithdrawReasonListPayload } from './interfaces/payloads/IMemberWithdrawReasonListPayload';
import MemberRequestBuilder from './MemberRequestBuilder';

const log = debug('sunny:MemberActions');

export default class MemberActions extends BaseActions<MemberRequestBuilder> {
  /** 회원 닉네임 변경 */
  changeNickName(params: IMemberChangeNickNameRequestParams): Promise<ResponseEither<IMemberChangeNickNamePayload>> {
    log('Changing for member nickname');
    return execAction('member change nickname', this.requestBuilder.createPutChangeNickName(params));
  }

  /** 회원 정보변경 비밀번호 확인 */
  confirmPassword(params: IMemberConfirmPasswordRequestParams): Promise<ResponseEither<IMemberConfirmPasswordPayload>> {
    log('Confirm for member password');
    return execAction('member confirm password', this.requestBuilder.createPostConfirmPassword(params));
  }

  /** 회원 비밀번호 변경 */
  changePassword(params: IMemberChangePasswordRequestParams): Promise<ResponseEither<IMemberChangePasswordPayload>> {
    log('Change for member password');
    return execAction('member change password', this.requestBuilder.createPutChangePassword(params));
  }

  /** 회원 약관동의 변경 */
  changeAgree(params: IMemberChangeAgreeRequestParams): Promise<ResponseEither<IMemberChangeAgreePayload>> {
    log('Change for member Agreement');
    return execAction('member change agreement', this.requestBuilder.createPostChangeAgree(params));
  }

  /** 회원 이메일 변경 */
  changeEmail(params: IMemberChangeEmailRequestParams): Promise<ResponseEither<IMemberChangeEmailPayload>> {
    log('Change for member email');
    return execAction('member change email', this.requestBuilder.createPostChangeEmail(params));
  }

  /** 회원 탈퇴 이유 리스트 조회 */
  withdrawReasonList(
    params: IMemberWithdrawReasonListRequestParams,
  ): Promise<ResponseEither<IMemberWithdrawReasonListPayload>> {
    log('retrive for withdraw reason list');
    return execAction('member withdraw reason list', this.requestBuilder.createGetWithdrawReasonList(params));
  }

  /** 회원 탈퇴 */
  withdraw(params: IMemberWithdrawRequestParams): Promise<ResponseEither<MemberWithdrawPayload>> {
    log('withdraw for member');
    return execAction('member withdraw', this.requestBuilder.createPostWithdraw(params));
  }

  /** Milk 연동 여부 확인 */
  checkMilkConnection(
    params?: IMemberCheckMilkConnectionRequestParams,
  ): Promise<ResponseEither<IMemberCheckMilkConnectionPayload>> {
    log('checking milk connection');
    return execAction('checking milk connection', this.requestBuilder.createGetCheckMilkConnection(params));
  }

  /** Milk 앱 회원 연동 */
  connectWithMilk(params: IMemberMilkConnectRequestParams): Promise<ResponseEither<IMemberMilkConnectPayload>> {
    log('Connecting with Milk');
    return execAction<IMemberMilkConnectPayload>(
      'milk connection',
      this.requestBuilder.createPostConnectWithMilk(params),
    );
  }

  /** Milk 앱 연동 해제 */
  disconnectMilk(params?: IMemberMilkDisconnectRequestParams): Promise<ResponseEither<IMemberMilkDisconnectPayload>> {
    log('Disconnecting Milk');
    return execAction<IMemberMilkDisconnectPayload>(
      'milk disconnection',
      this.requestBuilder.createPostDisconnectMilk(params),
    );
  }

  /** Email 수신거부 */
  unsubscribe(params?: IMemberUnsubscribeRequestParams): Promise<ResponseEither<IMemberUnsubscribePayload>> {
    log('Unsubscribe Email');
    return execAction<IMemberUnsubscribePayload>(
      'email unsubscribe',
      this.requestBuilder.createPostUnsubscribe(params),
    );
  }

  /** My혜택 */
  myBenefit(params: IMemberMyBenefitRequestParams): Promise<ResponseEither<IMemberMyBenefitPayload>> {
    log('My Benefit');
    return execAction<IMemberMyBenefitPayload>('my benefit', this.requestBuilder.createGetMyBenefit(params));
  }

  /** 소셜 사용자 정보로 가입된 야놀자 회원 정보 조회 */
  socialFind(params: IMemberSocialFindRequestParams): Promise<ResponseEither<IMemberSocialFindPayload>> {
    log('Social find');
    return execAction<IMemberSocialFindPayload>('Social find', this.requestBuilder.createPostSocialFind(params));
  }

  /** 소셜 연동 */
  socialLink(params: IMemberSocialLinkRequestParams): Promise<ResponseEither<IMemberSocialLinkPayload>> {
    log('Social link');
    return execAction<IMemberSocialLinkPayload>('Social link', this.requestBuilder.createPostSocialLink(params));
  }

  /** 소셜 연동 해제 */
  socialUnlink(params: IMemberSocialUnlinkRequestParams): Promise<ResponseEither<IMemberSocialUnlinkPayload>> {
    log('Social unlink');
    return execAction<IMemberSocialUnlinkPayload>(
      'Social unlink',
      this.requestBuilder.createDeleteSocialUnlink(params),
    );
  }

  /** 소셜 로그인 */
  socialLogin(params: IMemberSocialLoginV2RequestParams): Promise<ResponseEither<IMemberSocialLoginV2Payload>> {
    log('Social login V2');
    return execAction<IMemberSocialLoginV2Payload>(
      'Social login V2',
      this.requestBuilder.createPostSocialLogin(params),
    );
  }

  /** 야놀자 로그인 (소셜연동) */
  socialLoginWithLink(
    params: IMemberSocialLoginWithLinkRequestParams,
  ): Promise<ResponseEither<IMemberSocialLoginWithLinkPayload>> {
    log('Social login with link');
    return execAction<IMemberSocialLoginWithLinkPayload>(
      'Social login with link',
      this.requestBuilder.createPostSocialLoginWithLink(params),
    );
  }

  /** 소셜 회원 가입 */
  socialJoin(params: IMemberSocialJoinRequestParams): Promise<ResponseEither<IMemberSocialJoinPayload>> {
    log('Social join');
    return execAction<IMemberSocialJoinPayload>('Social join', this.requestBuilder.createPostSocialJoin(params));
  }

  /** 연동 정보 조회 */
  socialLinkInfo(params: IMemberSocialLinkInfoRequestParams): Promise<ResponseEither<IMemberSocialLinkInfoPayload>> {
    log('Social link info');
    return execAction<IMemberSocialLinkInfoPayload>(
      'Social link info',
      this.requestBuilder.createGetSocialLinkInfo(params),
    );
  }

  /** ywt 와 social data 를 비교하여 유효한지 검증 */
  socialLinkValidate(params: IMemberSocialLinkValidateRequestParams): Promise<ResponseEither<IMemberSocialLinkValidatePayload>> {
    log('Social link validate');
    return execAction<IMemberSocialLinkValidatePayload>(
      'Social link validate',
      this.requestBuilder.createPostSocialLinkValidate(params),
    );
  }

  /** 라이브방송 회원 연동 */
  liveCommerceToken(params: IMemberLiveCommerceTokenRequestParams): Promise<ResponseEither<IMemberLiveCommerceTokenPayload>> {
    log('Live Commerce Token');
    return execAction('live commerce token', this.requestBuilder.createPostLiveCommerceToken(params));
  }
}
